import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { OrderService } from '../../../services/order.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TabsModule } from 'primeng/tabs';
import {
  EligibilityAssessment,
  Order,
  OrderDocument,
  OrderDocumentType,
  OrderStatus,
} from '../../../models/order.model';
import { Customer } from '../../../models/customer.model';
import { PhoneFormatPipe } from '../../../../shared/pipes/format-phone.pipe';
import { OrderDetailsStateService } from './services/order-details-state.service';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { OrderDetailsDocumentsComponent } from './components/order-details-documents/order-details-documents.component';
import { OrderDetailsPaymentComponent } from './components/order-details-payment/order-details-payment.component';
import { OrderDetailsContractComponent } from './components/order-details-contract/order-details-contract.component';
import { OrderDetailsProductComponent } from './components/order-details-product/order-details-product.component';
import { OrderDetailsShipmentComponent } from './components/order-details-shipment/order-details-shipment.component';
import { OrderDetailsTabViewComponent } from './components/order-details-tab-view/order-details-tab-view.component';
import { OrderCancelDialogComponent } from './components/order-cancel-dialog/order-cancel-dialog.component';
import { OrderCancellationCardComponent } from './components/order-cancellation-card/order-cancellation-card.component';
import {
  canCancelOrder,
  getEffectiveOrderStatus,
  isOrderCancelled,
} from './utils/order-details.util';

export enum OrderDetailsTabEnum {
  Documents = 1,
  Payment,
  Product,
  Contract,
  Shipment,
}

@Component({
  selector: 'app-order-details',
  imports: [
    BreadcrumbModule,
    ButtonModule,
    TabsModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    TagStatusComponent,
    OrderDetailsDocumentsComponent,
    OrderDetailsPaymentComponent,
    OrderDetailsContractComponent,
    OrderDetailsProductComponent,
    OrderDetailsShipmentComponent,
    OrderDetailsTabViewComponent,
    OrderCancelDialogComponent,
    OrderCancellationCardComponent,
  ],
  providers: [PhoneFormatPipe],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss',
})
export class OrderDetailsComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private orderService = inject(OrderService);
  private messageService = inject(MessageService);
  private phoneFormatPipe = inject(PhoneFormatPipe);
  private readonly orderDetailsState = inject(OrderDetailsStateService);

  order = signal<Order | null>(null);
  eligibilityAssessment = signal<EligibilityAssessment | null>(null);
  loading = signal(false);

  showTabView = computed(() => {
    if (!this.order()) return false;
    const currentStatus = this.getCurrentStatus();

    return [
      OrderStatus.Rejected,
      OrderStatus.Shipped,
      OrderStatus.Delivered,
      OrderStatus.Cancelled,
    ].includes(currentStatus);
  });

  isReadOnly = computed(() => isOrderCancelled(this.order()));

  canCancel = computed(() => canCancelOrder(this.order()));

  cancelDialogVisible = signal(false);
  private orderLoaded = false;

  accessibleSteps = computed(() => this.getAccessibleSteps());

  Step = OrderDetailsTabEnum;
  protected currentStep = signal<OrderDetailsTabEnum>(
    OrderDetailsTabEnum.Documents,
  );
  protected lastStep = signal<OrderDetailsTabEnum>(
    OrderDetailsTabEnum.Documents,
  );

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) this.loadData(id);
  }

  get orderNumber(): string {
    return this.order()?.number ?? this.order()?.number ?? '-';
  }

  get statusText(): string {
    const status = this.order()?.status;
    return status ? this.orderService.getStatusDescription(status) : '-';
  }

  get statusTag(): string {
    const status = this.order()?.status;
    return status ? this.orderService.getStatusTag(status) : '';
  }

  get product(): any {
    return this.order()?.product;
  }

  get customer(): Customer {
    return this.order()?.customer as Customer;
  }

  get documents(): any[] {
    return (this.order()?.documents ??
      this.order()?.documents ??
      []) as OrderDocument[];
  }

  get payment(): any {
    return null;
  }

  get primaryEmail(): string {
    const c = this.customer;
    if (!c) return '-';

    const list = c.electronicAddresses ?? [];
    return list?.[0]?.address ?? '-';
  }

  get primaryPhone(): string {
    const c = this.customer;
    if (!c) return '-';

    const list = c.phones ?? [];
    if (list.length <= 0) return '-';

    const phone = list[0];
    const countryCode = phone!.countryCode;
    const phoneFormatted = this.phoneFormatPipe.transform(
      phone!.number,
      countryCode,
      { includeCountryCode: true },
    );
    return phoneFormatted;
  }

  reloadOrder(): void {
    if (this.order()) this.loadData(this.order()!.id);
  }

  getDocumentNameByType(type: OrderDocumentType) {
    switch (type) {
      case OrderDocumentType.ProofOfAddress:
        return 'Comprovante de Residência';
      case OrderDocumentType.BankStatement:
        return 'Comprovante Bancário';
      default:
        return 'Documento';
    }
  }

  goToStep(to?: OrderDetailsTabEnum) {
    if (!this.canAccessStep(to!)) return;

    if (to) {
      this.lastStep.set(this.currentStep());
      this.currentStep.set(to);
    } else {
      this.lastStep.set(this.currentStep());
      this.currentStep.update((current) => {
        const next = current + 1;
        return next > 5 ? current : next;
      });
    }
  }

  nextStep() {
    const next = this.currentStep() + 1;
    const to = next > 5 ? this.currentStep() : next;
    if (!this.canAccessStep(to!)) return;

    this.lastStep.set(this.currentStep());
    this.currentStep.set(to);
  }

  loadData(id: string): void {
    this.loading.set(true);

    this.orderService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const order = response.data as Order;
        this.order.set(order);

        if (this.isReadOnly()) {
          this.syncStepWithCancelledOrder();
        } else if (
          this.currentStep() === OrderDetailsTabEnum.Product &&
          this.getCurrentStatus() !== OrderStatus.Reserved
        ) {
          this.currentStep.set(OrderDetailsTabEnum.Contract);
        }

        this.orderLoaded = true;

        this.eligibilityAssessment.set(order?.eligibilityAssessment);
        this.orderDetailsState.publishEligibilityAssessment(
          order?.eligibilityAssessment,
        );
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse && e.status !== 404) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o pedido',
            life: 3000,
          });
        }
      },
    });
  }

  isStepClickable(step: OrderDetailsTabEnum): boolean {
    return !this.isReadOnly() || this.accessibleSteps().includes(step);
  }

  openCancelDialog(): void {
    this.cancelDialogVisible.set(true);
  }

  private syncStepWithCancelledOrder(): void {
    const steps = this.accessibleSteps();
    const keepCurrent = this.orderLoaded && steps.includes(this.currentStep());

    if (!keepCurrent) this.currentStep.set(steps[steps.length - 1]);
  }

  private canAccessStep(step: OrderDetailsTabEnum): boolean {
    if (!this.order()) return false;
    if (step === undefined || step === OrderDetailsTabEnum.Documents)
      return true;

    if (this.getAccessibleSteps().includes(step)) return true;

    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção',
      detail:
        'Essa etapa só pode ser acessando quando a etapa anterior for concluída.',
      life: 3000,
    });
    return false;
  }

  private getAccessibleSteps(): OrderDetailsTabEnum[] {
    const order = this.order();
    if (!order) return [OrderDetailsTabEnum.Documents];

    switch (getEffectiveOrderStatus(order)) {
      case OrderStatus.DocumentsPending:
      case OrderStatus.DocumentsSubmitted:
      case OrderStatus.UnderReview:
      case OrderStatus.Rejected:
        return [OrderDetailsTabEnum.Documents];

      case OrderStatus.PaymentPending:
        return [OrderDetailsTabEnum.Documents, OrderDetailsTabEnum.Payment];

      case OrderStatus.Reserved:
        return [
          OrderDetailsTabEnum.Documents,
          OrderDetailsTabEnum.Payment,
          OrderDetailsTabEnum.Product,
        ];

      case OrderStatus.ContractPending:
      case OrderStatus.Confirmed:
        return [
          OrderDetailsTabEnum.Documents,
          OrderDetailsTabEnum.Payment,
          OrderDetailsTabEnum.Product,
          OrderDetailsTabEnum.Contract,
        ];

      case OrderStatus.Preparing:
      case OrderStatus.Shipped:
      case OrderStatus.Delivered:
      case OrderStatus.Cancelled:
        return [
          OrderDetailsTabEnum.Documents,
          OrderDetailsTabEnum.Payment,
          OrderDetailsTabEnum.Product,
          OrderDetailsTabEnum.Contract,
          OrderDetailsTabEnum.Shipment,
        ];

      default:
        return [OrderDetailsTabEnum.Documents];
    }
  }

  private getCurrentStatus(): OrderStatus {
    return this.order()!.status;
  }
}
